/**
 * Loads the Segment analytics.js snippet.
 * Simplified version of platform-app's load-segment.ts without Amplitude/Braze integrations.
 */

import { Logger } from '../monitoring';

import { getSalesIntegrationsOptions, isGpcOptedOutOfSale } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface SegmentUser {
  anonymousId: () => string | undefined;
  id: () => string | undefined;
}

interface SegmentAnalyticsStub {
  // Stub properties (used during loading)
  invoked?: boolean;
  initialize?: boolean;
  initialized?: boolean;
  methods?: string[];
  factory?: (method: string) => (...args: unknown[]) => SegmentAnalyticsStub;
  load?: (key: string, options?: object) => void;
  _loadOptions?: object;
  _writeKey?: string;
  SNIPPET_VERSION?: string;
  push: (args: unknown[]) => number;
  // Analytics methods (used after loading)
  track: (event: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void;
  identify: (userId?: string, traits?: Record<string, unknown>, options?: Record<string, unknown>) => void;
  page: (name?: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void;
  ready: (callback: () => void) => void;
  reset: () => void;
  setAnonymousId: (id: string) => void;
  user: () => SegmentUser | undefined;
  [key: string]: any;
}

declare global {
  interface Window {
    analytics?: SegmentAnalyticsStub;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

let loaded = false;

export const loadSegment = (key: string): void => {
  if (loaded) return;

  const analytics: SegmentAnalyticsStub = (window.analytics = window.analytics || ([] as unknown as SegmentAnalyticsStub));

  // If the real analytics.js is already on the page, return
  if (analytics.initialize) return;

  // If the snippet was invoked already, show an error
  if (analytics.invoked) {
    Logger.error('Segment snippet included twice.', undefined, new Error('Segment snippet included twice.'));
    return;
  }

  analytics.invoked = true;

  // A list of the methods in Analytics.js to stub
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'screen',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
    'register',
  ];

  // Define a factory to create stubs. These are placeholders
  // for methods in Analytics.js so that you never have to wait
  // for it to load to actually record data. The `method` is
  // stored as the first argument, so we can replay the data.
  analytics.factory = function (method: string) {
    return function (...args: unknown[]) {
      if (window.analytics?.initialized) {
        // Sometimes users assigned analytics to a variable before analytics is done loading, resulting in a stale reference.
        // If so, proxy any calls to the 'real' analytics instance.
        const methodFn = window.analytics[method];
        if (typeof methodFn === 'function') {
          return methodFn(...args);
        }
        return window.analytics;
      }
      const methodArgs = [...args];

      // Add buffered page context object so page information is always up-to-date
      if (['track', 'screen', 'alias', 'group', 'page', 'identify'].indexOf(method) > -1) {
        const canonical = document.querySelector("link[rel='canonical']");
        methodArgs.push({
          __t: 'bpc',
          c: (canonical && canonical.getAttribute('href')) || undefined,
          p: location.pathname,
          u: location.href,
          s: location.search,
          t: document.title,
          r: document.referrer,
        });
      }

      methodArgs.unshift(method);
      analytics.push(methodArgs);
      return analytics;
    };
  };

  // Create stubs for each method
  for (const method of analytics.methods) {
    analytics[method] = analytics.factory(method);
  }

  // Define a method to load Analytics.js from Melio's CDN
  analytics.load = function (writeKey: string, options?: object) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-global-segment-analytics-key', 'analytics');
    script.src = `https://analytics.melio.com/analytics.js/v1/${writeKey}/analytics.min.js`;

    // Add error handling to prevent script loading errors from breaking the page
    script.onerror = () => {
      Logger.warn('[Analytics] Failed to load Segment script', undefined, new Error('[Analytics] Failed to load Segment script'));
    };

    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(script, first);
    analytics._loadOptions = options;
  };

  analytics._writeKey = key;
  analytics.SNIPPET_VERSION = '5.2.1';

  // Block destinations that aren't needed for auth-app. Ad-sale destinations
  // (GTM, Facebook Pixel, Google Ads, AdWords, Twitter Ads) are intentionally
  // NOT blocked here - they load normally and are instead gated per-event via
  // the `integrations` option in Analytics.ts based on CCPA opt-out state.
  const blockedDestinations = {
    'Braze Web Mode (Actions)': false,
  };

  // Load Analytics.js with the key
  analytics.load(key, {
    obfuscate: true,
    storage: { stores: ['cookie', 'localStorage', 'memory'] },
    integrations: {
      ...blockedDestinations,
      'Segment.io': {
        deliveryStrategy: {
          strategy: 'batching',
          config: {
            size: 20,
            timeout: 1000,
          },
        },
      },
    },
  });

  // Make the first page call. This fires before the app can call Analytics.page(),
  // so it must independently respect the GPC signal for the CCPA-excluded destinations
  // (a DB-based opt-out isn't known yet at this point - that's applied to later calls
  // once the user identifies via identifyUser()).
  const initialPageIntegrations = getSalesIntegrationsOptions(isGpcOptedOutOfSale());
  analytics.page(undefined, undefined, initialPageIntegrations && { integrations: initialPageIntegrations });

  // Handle anonymous ID from URL params (for cross-domain tracking)
  const searchParams = new URLSearchParams(location.search);
  const anonymousId = searchParams.get('ajs_aid');
  if (anonymousId) {
    analytics.setAnonymousId(anonymousId);
  }

  loaded = true;
};
